import type { IconName } from '@fortawesome/fontawesome-common-types';
import {
  List,
  ListItemAvatar,
  ListItem,
  ListItemText,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Icon from '@components/Icon/Icon';

const messages = defineMessages({
  no: {
    id: 'GLOBAL.no',
    defaultMessage: 'No',
  },
  yes: {
    id: 'GLOBAL.yes',
    defaultMessage: 'Yes',
  },
});

interface ListItemToggleProps {
  /* Is this field enabled? */
  enabled?: boolean;
  /* Callback that fires when the field is (en/dis)abled */
  onToggle: (checked: boolean) => void;
  /* Primary text of form field */
  primary_text: string;
  /* Secondary text of form field */
  secondary_text?: string;
  /** Third line, under the secondary text, for a highlighted aside */
  tertiary_text?: string;
  /* Icon of form field */
  icon_name?: IconName;
  /* Color of form field icon */
  icon_color?: string;
  /** Is the form disabled?  */
  disabled?: boolean;
  /**
   * `card` (the default) stands the row up as its own elevated card.
   * `row` drops the wrapping list and the elevation so the caller can group
   * several rows inside one container of its own.
   */
  variant?: 'card' | 'row';
  'data-testid'?: string;
}

export default function ListItemToggle({
  enabled,
  onToggle,
  primary_text,
  secondary_text,
  tertiary_text,
  icon_name,
  icon_color,
  disabled,
  variant = 'card',
  'data-testid': dataTestId,
}: ListItemToggleProps) {
  const { formatMessage } = useIntl();
  const handleToggle = (checked: boolean) => onToggle(checked);
  const isRow = variant === 'row';

  const toggle = (
    <Stack direction="row" spacing={0} alignItems="center" flexShrink={0}>
      <Switch
        checked={enabled}
        disabled={disabled}
        // Without this the switch is an unlabelled checkbox: it announces its
        // on/off value with no indication of what it belongs to.
        inputProps={{ 'aria-label': primary_text }}
        onChange={(_, checked) => handleToggle(checked)}
        color="primary"
      />
      <Typography
        variant="subtitle1"
        color="textPrimary"
        // `subtitle1` maps to <h6>, which would announce the value as a
        // heading on every row.
        component="span"
        // Holds the switch still as the label swaps between Yes and No, so a
        // column of rows keeps its switches aligned.
        minWidth={isRow ? 28 : undefined}
      >
        {formatMessage(enabled ? messages.yes : messages.no)}
      </Typography>
    </Stack>
  );

  const content = (
    <>
      {icon_name && (
        <ListItemAvatar>
          <Icon
            color={icon_color ?? (enabled ? 'primary.main' : 'text.primary')}
            name={icon_name}
            size="lg"
            type="fad"
          />
        </ListItemAvatar>
      )}

      <ListItemText
        sx={isRow ? { my: 0 } : undefined}
        primary={
          <Typography
            variant="subtitle1"
            // As above: the title is already the switch's accessible name, so
            // making it a heading too has it announced twice.
            component="p"
            color={!isRow && enabled ? 'primary.main' : 'textPrimary'}
          >
            {primary_text}
          </Typography>
        }
        secondary={
          secondary_text || tertiary_text ? (
            <Typography
              variant="body2"
              color="text.secondary"
              component="p"
              mt={isRow ? 0.5 : 0}
            >
              {secondary_text}
              {tertiary_text && (
                <Typography
                  variant="caption"
                  color="primary"
                  // A <span> so it stays valid inside the secondary <p>;
                  // `display: block` puts it on its own line regardless.
                  component="span"
                  display="block"
                  mt={secondary_text ? 0.5 : 0}
                >
                  {tertiary_text}
                </Typography>
              )}
            </Typography>
          ) : null
        }
      />
    </>
  );

  if (isRow) {
    return (
      // A <div>, not the default <li>: the caller supplies the container, and
      // a stray <li> outside a list is neither valid nor announced usefully.
      <ListItem
        alignItems="flex-start"
        component="div"
        data-testid={dataTestId}
        sx={{ px: 2.25, py: 1.75 }}
      >
        {content}
        {toggle}
      </ListItem>
    );
  }

  return (
    <List sx={{ my: 1.5 }}>
      <ListItem
        data-testid={dataTestId}
        sx={(theme) => ({
          backgroundColor: 'background.paper',
          borderRadius: 2,
          boxShadow: theme.shadows[1],
        })}
        secondaryAction={toggle}
      >
        {content}
      </ListItem>
    </List>
  );
}
